/**
 * ASCII Art Generator - Waves 波浪叠加效果
 *
 * Color Shift 颜色波动，波浪干涉条纹 + 色相偏移
 */

import { createNoise2D } from 'simplex-noise'
import type { WavesConfig } from './types'
import { applyHueShift, clamp } from './helpers'

// ============================================
// 噪声生成器
// ============================================

// 创建 2D simplex noise 实例
const noise2D = createNoise2D()

// ============================================
// 纯函数计算
// ============================================

/**
 * 计算波浪干涉值
 * @param x 像素x坐标
 * @param y 像素y坐标
 * @param time 时间
 * @param frequency 频率
 * @param waveCount 波源数量
 * @returns 干涉值 (-1 到 1)
 */
export function calculateWaveInterference(
  x: number,
  y: number,
  time: number,
  frequency: number,
  waveCount: number
): number {
  let interference = 0

  // 多个波源叠加
  for (let i = 0; i < waveCount; i++) {
    // 每个波源有不同的相位和方向
    const phase = time * (1 + i * 0.3)
    const angle = (i * Math.PI * 2) / waveCount

    // 波源位置偏移
    const sourceX = x + Math.cos(angle) * 100
    const sourceY = y + Math.sin(angle) * 100

    // 使用 simplex noise 生成平滑波动
    const noiseValue = noise2D(
      sourceX * frequency + phase,
      sourceY * frequency + phase
    )

    interference += noiseValue
  }

  // 平均化干涉值
  return interference / waveCount
}

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用波浪叠加效果到 Canvas
 * @param canvas 目标画布
 * @param config 波浪配置
 */
export function applyWaves(
  canvas: HTMLCanvasElement,
  config: WavesConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { strength, frequency, speed, hueShift, waveCount, time } = config

  // 获取当前画布内容
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  // 时间因子
  const timeFactor = time * speed

  // 处理每个像素
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4

      // 计算波浪干涉值
      const interference = calculateWaveInterference(
        x, y, timeFactor, frequency, waveCount
      )

      // 计算色相偏移
      const hueOffset = interference * hueShift * strength

      // 应用色相偏移到像素
      if (hueShift > 0) {
        const shifted = applyHueShift(data[i], data[i + 1], data[i + 2], hueOffset)
        data[i] = clamp(shifted.r, 0, 255)
        data[i + 1] = clamp(shifted.g, 0, 255)
        data[i + 2] = clamp(shifted.b, 0, 255)
      }

      // 同时应用亮度波动（干涉图案）
      const brightnessFactor = 1 - strength * Math.abs(interference) * 0.3
      data[i] = clamp(data[i] * brightnessFactor, 0, 255)
      data[i + 1] = clamp(data[i + 1] * brightnessFactor, 0, 255)
      data[i + 2] = clamp(data[i + 2] * brightnessFactor, 0, 255)
    }
  }

  // 写回画布
  ctx.putImageData(imageData, 0, 0)
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * 波浪效果类
 */
export class WavesEffect {
  private config: WavesConfig
  private time: number = 0

  constructor(config: WavesConfig) {
    this.config = config
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 更新配置
   */
  updateConfig(config: WavesConfig): void {
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    const config = { ...this.config, time: this.time }
    applyWaves(canvas, config)
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建波浪配置
 */
export function createWavesConfigFromSettings(
  strength: number,
  frequency: number,
  speed: number,
  hueShift: number,
  waveCount: number
): WavesConfig {
  return {
    strength: clamp(strength, 0, 1),
    frequency: clamp(frequency, 0.01, 0.2),
    speed: clamp(speed, 0.1, 5),
    hueShift: clamp(Math.round(hueShift), 0, 360),
    waveCount: clamp(Math.round(waveCount), 2, 5),
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认波浪配置 - 参数已调整使效果更明显 */
export const DEFAULT_WAVES_CONFIG: WavesConfig = {
  strength: 0.6,
  frequency: 0.08,
  speed: 1.5,
  hueShift: 60,
  waveCount: 3,
  time: 0
}